/* eslint-disable complexity */
'use client';

import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteSlots,
  Box,
  ClickAwayListener,
  UseAutocompleteProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Image from 'next/image';
import {
  FC,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import React from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { AbgTypography, AlertType, CarIcon } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

// import { useBookingWidgetContext } from '../../organisms/BookingWidget/hooks';
import { useBookingWidgetFormContext } from '../../organisms/BookingWidget/hooks/use-booking-widget-form-context';
import { ConfigItemFormContainerMobile } from '../../organisms/BookingWidget/molecules/config-item/ConfigItemFormContainerMobile';
import { HeaderContainerContext } from '../../organisms/HeaderContainer';
import { useAnalyticsError } from '../../shared/analytics/hooks/index';
import { useAnalyticsLocationSearch } from '../../shared/analytics/hooks/index';
import { getSearchResultsAnalyticsCounts } from '../../shared/analytics/utils/get-search-results-analytics-counts';
import { sanitizeHtml } from '../../shared/utils';
import {
  bookingWidgetEventsStoreSelector,
  bookingWidgetFormStoreSelector,
} from '../../store';

import { LocationTextField } from './atoms/location-text-field/LocationTextField';
import { OptionWrapper } from './atoms/option-wrapper/OptionWrapper';
import { useLocationSearchOptions } from './hooks/location-search-options';
import { saveRecentOptions } from './hooks/location-search-options/location-search-options.utils';
import { useDetailsLeft } from './hooks/use-details-left';
import { useGetLocationMessage } from './hooks/use-get-location-message';
import { useShowAllKeys } from './hooks/use-show-all-keys';
import { useSuggestionGroups } from './hooks/use-suggestion-groups';
import { LocationDetailsMobile } from './molecules/location-details/LocationDetailsMobile';
import { hasLocationDetails } from './molecules/location-details/utils';
import { LocationSearchMobilePopper } from './molecules/location-search-popper/LocationSearchMobilePopper';
import { LocationSearchPopper } from './molecules/location-search-popper/LocationSearchPopper';
import NoMatchesCard from './molecules/no-matches-option/NoMatchesCard';
import { SuggestionOption } from './molecules/suggestion-option/SuggestionOption';
import { MINIMUM_SEARCH_QUERY_LENGTH } from './constants';
import {
  LocationSearchProperties,
  LocationSuggestionOption,
  SuggestionOptionsMode,
} from './location-search.types';
import {
  getFormattedValueByLocationSiteType,
  isSuggestion,
} from './location-suggestion.utils';
import { LocationSearchWrapper } from './LocationSearch.styled';
import { avisFirstDeliveryLabelContainer } from './LocationSearch.styling';

import avisFirstLogo from '../../../assets/avis-first-logo.svg';

// TODO: refactor to reduce code lines number
/* eslint-disable max-lines */
export const LocationSearch: FC<LocationSearchProperties> = ({
  name,
  control,
  inputIcon = CarIcon,
  loadCurrentOptions,
  loadOptions,
  errors,
  allowGeolocation,
  uiTextData: {
    placeholder,
    loadingText,
    useCurrentLocationLabel,
    useCurrentLocationFoundErrorMessage,
    groupBackButtonLabel,
    noMatchLabel,
    reviseSearchLabel,
    browseForLocationLabel,
    helperSearchText,
    multipleLocationsLabel,
    avisFirstSelectAreasLabel,
    avisFirstDeliveryToYourAddressLabel,
    avisFirstBookingwidgetAvailableLabel,
    avisFirstDeliveryPickupLabel,
    avisFirstCurbsideDeliveryLabel,
    avisFirstAtHomeDeliveryLabel,
    ...otherLabels
  },
  endAdornment = null,
  testIdPrefix = '',
  optionTestIdBuilder,
  groupLimits: groupLimitsFromAem = {},
  includeSavedLocations = true,
  includeRecentLocations = true,
  showLocationDetailsFeature = false,
  showStationDirection = false,
  onSelectLocation,
  onLocationSelected,
  onInputChange,
  shouldClearField = false,
  currentReservationLocation,
  label = '',
  withLabel = false,
  disabled = false,
  excludeVehicleCategories,
  onClickOutside,
  onCitySelect,
  isAFLocalMarketChangesEnabled,
  isAFLocalMarketStepOneEnabled,
  isAFLocalMarketStepOneOtherLocationLabelEnabled,
  hideLocationIcon = false,
}) => {
  const { navigationLinks, origin } = usePageContext();

  // const { isAFLocalMarketChangesEnabled } = useBookingWidgetContext();

  const { getLocationMessage } = useGetLocationMessage();
  const openBookingWidgetAlert =
    bookingWidgetFormStoreSelector.use.openBookingWidgetAlert();
  const { toggleBookingWidgetAlert, locationMessage, setLocationMessage } =
    useBookingWidgetFormContext();
  const findLocationUrl = new URL(
    `${navigationLinks.locations}/find-a-location`,
    origin,
  );

  const recordAnalyticsLocationSearch = useAnalyticsLocationSearch();
  const recordAnalyticsError = useAnalyticsError();
  const {
    field,
    fieldState: { error: fieldError },
  } = useController({
    name,
    ...(control ? { control, defaultValue: null } : {}),
  });
  const value = field.value as LocationSuggestionOption | null;
  const lastTrackedError = useRef<string | null>(null);
  const mobileTriggerInputReference = useRef<HTMLInputElement>(null);

  const { clearErrors, setValue: setFormValue } = useFormContext() || {};

  const { isBookingWidgetBackdropVisible, setEmbeddedWidgetState } = useContext(
    HeaderContainerContext,
  );

  const [currentSearchMode, setCurrentSearchMode] =
    useState<SuggestionOptionsMode>(SuggestionOptionsMode.KeywordsOptions);

  const [inputValue, setInputValue] = useState(value?.locationName ?? '');
  const [inputReason, setInputReason] = useState(
    'input' as AutocompleteInputChangeReason,
  );
  const [inFocus, setInFocus] = useState(false);
  const [open, setOpen] = useState(false);
  const focusTransferReference = useRef(false);

  // Sync inputValue when the form value is set externally (e.g. useLocations on VLP
  // or location-list selection). The field may still be marked focused while the
  // mobile drawer opens, so focus cannot be used to detect a user edit here.
  const prevLocationName = useRef(value?.locationName);
  const currentLocationName = value?.locationName ?? '';
  const isSyncingExternalValue =
    currentLocationName !== prevLocationName.current;
  const effectiveInputValue = isSyncingExternalValue
    ? currentLocationName
    : inputValue;
  const effectiveInputReason = isSyncingExternalValue ? 'reset' : inputReason;

  useEffect(() => {
    if (currentLocationName !== prevLocationName.current) {
      prevLocationName.current = currentLocationName;
      setInputReason('reset');
      setInputValue(currentLocationName);
    }
  }, [currentLocationName]);

  const [locationDetailOption, setLocationDetailOption] =
    useState<LocationSuggestionOption | null>(null);

  // Mobile detection and drawer state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileDetailsDrawerOpen, setMobileDetailsDrawerOpen] = useState(false);
  const [mobileDetailsOption, setMobileDetailsOption] =
    useState<LocationSuggestionOption | null>(null);

  const { selectedGroup, renderGroup, handleGroupClear } = useSuggestionGroups({
    groupLimitsFromAem,
    requestedInputValue: inputValue,
    labels: {
      groupBackButtonLabel,
      ...otherLabels,
    },
  });

  const emit = bookingWidgetEventsStoreSelector.use.emit();

  const handleClearField = useCallback(() => {
    // Use setValue with shouldValidate: false instead of field.onChange(null)
    // to prevent async re-validation on a touched field (mode: 'onTouched').
    // field.onChange(null) triggers the zod resolver asynchronously, which would
    // set an error after any synchronous clearErrors() call.
    setFormValue?.(name, null, { shouldValidate: false });
    clearErrors?.(name);
    name === 'pickup' && emit(name, null);
    name === 'dropoff' && emit(name, null);

    name === 'pickup' && setLocationMessage('');

    setInFocus(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    options,
    loading,
    noMatchFound,
    setOptions,
    setNoMatchFound,
    handleCurrentLocationOption,
    handleQueryPermissions,
  } = useLocationSearchOptions({
    formFieldName: name,
    loadOptions,
    loadCurrentOptions,
    allowGeolocation,
    includeSavedLocations,
    currentSearchMode,
    setCurrentSearchMode,
    useCurrentLocationLabel,
    useCurrentLocationFoundErrorMessage,
    inputValue: effectiveInputValue,
    setInputValue,
    selectedValue: value,
    selectedGroup,
    handleClearField,
    setOpen,
    currentReservationLocation,
    includeRecentLocations,
    inputReason: effectiveInputReason,
    excludeVehicleCategories,
  });

  const handleLocationDetailClear = () => {
    setLocationDetailOption(null);
  };

  const handleMobileInfoClick = useCallback(
    (option: LocationSuggestionOption) => {
      setMobileDetailsOption(option);
      setMobileDetailsDrawerOpen(true);
    },
    [],
  );

  const handleMobileDetailsDrawerClose = useCallback(() => {
    setMobileDetailsDrawerOpen(false);
    setMobileDetailsOption(null);
  }, []);

  // @TODO: create use-suggestion-errors hook
  // Also suppress error while the mobile drawer or desktop dropdown is open —
  // handleBlur fires (setting identifier: null) before the option click registers,
  // so we must not surface a validation error while the user is still interacting.
  // const hasIncorrectValue = inFocus || loading ? false : Boolean(fieldError);
  // const emptyFieldError = hasIncorrectValue && inputValue.length === 0;
  const hasIncorrectValue =
    inFocus || loading || mobileDrawerOpen || open
      ? false
      : Boolean(fieldError);
  const emptyFieldError = hasIncorrectValue && inputValue.length === 0;

  // @TODO: create use-suggestion-errors hook
  const renderError = () => {
    if (fieldError?.message) {
      if (lastTrackedError.current !== fieldError.message) {
        recordAnalyticsError({
          error_text: fieldError.message,
          error_type: 'alert',
        });
        lastTrackedError.current = fieldError.message;
      }
      return (
        <span
          data-testid={`${testIdPrefix}-error-message`}
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(fieldError.message) }}
        />
      );
    }

    const error = emptyFieldError
      ? errors.emptyValue
      : hasIncorrectValue
        ? errors.incorrectValue
        : null;

    if (error) {
      const sanitizedError = sanitizeHtml(error) as string;

      if (lastTrackedError.current !== sanitizedError) {
        recordAnalyticsError({
          error_text: sanitizedError,
          error_type: 'alert',
        });
        lastTrackedError.current = sanitizedError;
      }

      return (
        <span
          data-testid={`${testIdPrefix}-error-message`}
          dangerouslySetInnerHTML={{ __html: sanitizedError }}
        />
      );
    }

    return null;
  };

  useEffect(() => {
    if (!fieldError?.message && !emptyFieldError && !hasIncorrectValue) {
      lastTrackedError.current = null;
    }
  }, [fieldError?.message, emptyFieldError, hasIncorrectValue]);

  const handleFocus = () => {
    if (isMobile) {
      setMobileDrawerOpen(true);
      setInFocus(true);
      setNoMatchFound(false);
      handleQueryPermissions();

      if (inputValue === '') {
        setOptions([]);
      }

      if (!isBookingWidgetBackdropVisible) {
        setEmbeddedWidgetState?.(true);
      }
      return;
    }

    setInFocus(true);
    setNoMatchFound(false);
    handleQueryPermissions();
    setOpen(true);

    if (inputValue === '') {
      setOptions([]);
    }

    if (!isBookingWidgetBackdropVisible) {
      setEmbeddedWidgetState?.(true);
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
    setInFocus(false);
    handleGroupClear();
    handleLocationDetailClear();

    // ARGWE-26604: Trigger focus→blur on the trigger input so the browser natively resets
    // scrollLeft to 0 (same mechanism desktop uses via blurOnSelect).
    // This is a cosmetic improvement — can be safely removed if it causes side effects.
    requestAnimationFrame(() => {
      if (mobileTriggerInputReference.current) {
        mobileTriggerInputReference.current.focus();
        mobileTriggerInputReference.current.blur();
      }
    });
  };

  const handleBlur = () => {
    if (focusTransferReference.current) return;
    if (
      inputValue &&
      ((value && inputValue !== value.locationName) || !value)
    ) {
      field.onChange({
        identifier: null,
        suggestionTypeCode: 'city',
        locationName: inputValue === useCurrentLocationLabel ? '' : inputValue,
      });
    }

    handleGroupClear();
    handleLocationDetailClear();
    setInFocus(false);
    field.onBlur();
  };

  const handleClose: UseAutocompleteProps<
    LocationSuggestionOption,
    false,
    false,
    true
  >['onClose'] = (_, reason) => {
    if (reason === 'blur' && focusTransferReference.current) return;

    setOpen(false);
  };

  const handleHighlightChange: UseAutocompleteProps<
    LocationSuggestionOption,
    false,
    false,
    true
  >['onHighlightChange'] = (_, option, reason) => {
    const isHoverOrKeyboardTrigger = ['mouse', 'keyboard'].includes(reason);

    if (!option || !isHoverOrKeyboardTrigger) {
      return;
    }

    const notApplicableTypeCodes = ['Error'];
    const notApplicableSuggestionTypeCodes = ['ABG-AVIS-FIRST-DELIVERY'];

    if (!option.typeCode || notApplicableTypeCodes.includes(option.typeCode)) {
      handleLocationDetailClear();

      return;
    }

    // Don't show details for ABG-AVIS-FIRST-DELIVERY suggestions as they are not base actual locations and would cause confusion.
    if (notApplicableSuggestionTypeCodes.includes(option.suggestionTypeCode)) {
      handleLocationDetailClear();

      return;
    }

    // Don't show details for ABG-AVIS-FIRST-DELIVERY suggestions as they are not base actual locations and would cause confusion.
    if (option.suggestionTypeCode === 'RECENT' && option.deliveryAddress) {
      handleLocationDetailClear();

      return;
    }

    // City suggestions (and similar non-station entries) carry no address,
    // hours, or phones, so the details panel would render empty. Hide it.
    if (!hasLocationDetails(option)) {
      handleLocationDetailClear();

      return;
    }

    setLocationDetailOption(option);
  };

  const focusRightDetails = () => {
    const detailsPanel = document.querySelector<HTMLElement>(
      '[data-testid="location-details-root"]',
    );
    if (!detailsPanel) {
      focusTransferReference.current = false;
      return;
    }

    const firstFocusable = detailsPanel.querySelector<HTMLElement>(
      'a[href],button,[role="button"],input,select,textarea,[tabindex]:not([tabindex="-1"])',
    );
    if (!firstFocusable) {
      focusTransferReference.current = false;
      return;
    }

    firstFocusable.focus();
    document.addEventListener(
      'focusin',
      () => (focusTransferReference.current = false),
      { once: true, capture: true },
    );
  };

  useDetailsLeft(open, locationDetailOption, focusTransferReference);
  useShowAllKeys(open, focusTransferReference);

  const handleAutocompleteKeys = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTransferReference.current = true;
      requestAnimationFrame(focusRightDetails);
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const activeOptionElement = document.querySelector<HTMLLIElement>(
      'li[role="option"].Mui-focused',
    );
    const activeOptionLink =
      activeOptionElement?.querySelector<HTMLAnchorElement>('a[href]') ?? null;
    if (!activeOptionLink) {
      // No dropdown option is highlighted — the user pressed Enter on free-typed
      // text. MUI Autocomplete (freeSolo) absorbs the keydown and never lets it
      // reach the native <form>, so the onSubmit handler (and the location
      // suggestions modal check inside it) would never fire.
      // Programmatically request a form submit so the full submit pipeline runs,
      // including tryOpenLocationSuggestionsModal().
      if (event.key === 'Enter') {
        const form = (event.currentTarget as HTMLElement).closest('form');
        form?.requestSubmit();
      }
      return;
    }

    event.preventDefault();
    activeOptionLink.click();
  };

  const handleChange = (
    _event: SyntheticEvent,
    newValue: string | LocationSuggestionOption | null,
  ) => {
    if (newValue === null) {
      field.onChange(null);

      name === 'pickup' && emit(name, null);
      name === 'dropoff' && emit(name, null);

      return;
    }
    if (typeof newValue === 'string') {
      return;
    }

    if (newValue.typeCode === 'Error') {
      return;
    }

    if (newValue.suggestionTypeCode === 'CURRENT') {
      setCurrentSearchMode(SuggestionOptionsMode.GeoLocationRequested);
      handleCurrentLocationOption();

      return;
    }

    saveRecentOptions(newValue);

    const valueWithSuggestionTypeCode = {
      ...newValue,
      suggestionTypeCode:
        getFormattedValueByLocationSiteType(newValue).suggestionTypeCode,
    };

    const { airports, cities, neighborhoods } =
      getSearchResultsAnalyticsCounts(options);

    recordAnalyticsLocationSearch({
      search_term: newValue.locationName,
      search_results: `${airports} | ${cities} | ${neighborhoods}`,
    });

    field.onChange(valueWithSuggestionTypeCode);

    name === 'pickup' && emit(name, valueWithSuggestionTypeCode);
    name === 'dropoff' && emit(name, valueWithSuggestionTypeCode);

    onSelectLocation?.(newValue);
    onLocationSelected?.();
    // Notify parent when a city (no mnemonic) is selected
    // BookingWidget uses this to trigger form submission
    if (!newValue?.mnemonic) {
      onCitySelect?.(newValue);
    }

    if (name === 'pickup' && newValue.mnemonic) {
      void getLocationMessage(newValue.mnemonic).then((response) => {
        setLocationMessage(response?.comment || '');
        return;
      });
    }

    // Close mobile drawer when user selects an option
    if (isMobile && mobileDrawerOpen) {
      handleMobileDrawerClose();
    }
  };

  const handleMobileLocationSelect = useCallback(() => {
    if (!mobileDetailsOption) {
      return;
    }
    setInputValue(mobileDetailsOption.locationName);
    handleChange({} as SyntheticEvent, mobileDetailsOption);
    handleMobileDetailsDrawerClose();
  }, [mobileDetailsOption, handleMobileDetailsDrawerClose]);

  useEffect(() => {
    if (inputValue.length > 0) {
      clearErrors?.(name);
    }
  }, [inputValue, clearErrors, name]);

  useEffect(() => {
    if (inputValue.length >= MINIMUM_SEARCH_QUERY_LENGTH) {
      handleGroupClear();
      handleLocationDetailClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (!showStationDirection) return;

    if (locationMessage) {
      openBookingWidgetAlert({
        title: '',
        type: AlertType.INFORMATION,
        description: locationMessage,
      });
    } else {
      toggleBookingWidgetAlert(false);
    }
  }, [
    locationMessage,
    openBookingWidgetAlert,
    toggleBookingWidgetAlert,
    showStationDirection,
  ]);

  useEffect(() => {
    if (
      currentSearchMode === SuggestionOptionsMode.GeoLocationOptions &&
      !open
    ) {
      setOptions([]);
      setCurrentSearchMode(SuggestionOptionsMode.KeywordsOptions);
      handleLocationDetailClear();
    }
  }, [open, currentSearchMode, setOptions, setCurrentSearchMode]);

  useEffect(() => {
    if (shouldClearField) {
      setInputValue('');
      setInputReason('input');
      onInputChange?.('');
      handleClearField();
    }
  }, [shouldClearField]);

  const handleCloseClickOutside = useCallback(() => {
    onClickOutside?.({ isEmptyInputValue: !inputValue?.trim() });
  }, [inputValue, onClickOutside]);

  if (isMobile) {
    return (
      <ClickAwayListener onClickAway={handleCloseClickOutside}>
        <LocationSearchWrapper>
          <LocationTextField
            disabled={disabled}
            onClick={handleFocus}
            value={value ? value.locationName : ''}
            location={value}
            defaultIcon={inputIcon}
            endAdornment={endAdornment}
            testIdSuffix="mobile-trigger-input"
            testIdPrefix={testIdPrefix}
            hasIncorrectValue={hasIncorrectValue}
            renderError={renderError}
            placeholder={placeholder}
            label={withLabel ? label : ''}
            hideLocationIcon={hideLocationIcon}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleFocus();
              }
            }}
            slotProps={{
              input: {
                readOnly: true,
              },
              htmlInput: {
                'aria-label': placeholder,
                ref: mobileTriggerInputReference,
              },
            }}
          />

          <ConfigItemFormContainerMobile
            testId={testIdPrefix}
            isOpen={mobileDrawerOpen}
            handleClose={handleMobileDrawerClose}
            drawerTitle={label}
          >
            <Autocomplete<LocationSuggestionOption, false, false, true>
              disabled={disabled}
              open={true}
              disablePortal
              freeSolo
              filterOptions={(x) => x} // disable default filter, filtering we'll be implemented in an async request
              filterSelectedOptions // hide selected value, since it is appended to the options array
              {...field}
              value={value}
              inputValue={inputValue}
              onChange={handleChange}
              onBlur={handleBlur}
              options={options as readonly LocationSuggestionOption[]}
              groupBy={
                noMatchFound ? undefined : (option) => option.suggestionGroup
              }
              renderGroup={renderGroup}
              slots={{
                popper:
                  LocationSearchMobilePopper as AutocompleteSlots['popper'],
              }}
              slotProps={{
                popper: {
                  // @ts-expect-error TS2353
                  'data-testid': `${testIdPrefix}-mobile-dropdown`,
                  showLocationDetailsFeature,
                  locationDetailOption,
                  uiTextData: {
                    ...otherLabels,
                    avisFirstCurbsideDeliveryLabel,
                    avisFirstAtHomeDeliveryLabel,
                  },
                },
              }}
              multiple={false}
              loading={loading}
              loadingText={loadingText}
              onHighlightChange={handleHighlightChange}
              onInputChange={(_event, newInputValue, reason) => {
                setInputReason(reason);
                setInputValue(newInputValue);
                onInputChange?.(newInputValue);
              }}
              renderInput={(parameters) => (
                <LocationTextField
                  disabled={disabled}
                  location={value}
                  defaultIcon={inputIcon}
                  endAdornment={endAdornment}
                  testIdSuffix="mobile-drawer-input"
                  testIdPrefix={testIdPrefix}
                  hasIncorrectValue={hasIncorrectValue}
                  renderError={renderError}
                  placeholder={placeholder}
                  label={label}
                  hideLocationIcon={hideLocationIcon}
                  slotProps={{
                    input: {
                      ...parameters.InputProps,
                    },
                    htmlInput: {
                      ...parameters.inputProps,
                      // ARGWE-26604: autoFocus removed — it caused the input to show
                      // the end of the pre-filled value instead of the beginning.
                    },
                  }}
                />
              )}
              getOptionLabel={(option) =>
                isSuggestion(option) ? option.locationName : option
              }
              renderOption={(
                { key, ...optionProperties },
                option,
                { index },
              ) => {
                // @TODO: refactor this condition
                if (!loading && option.typeCode === 'Error') {
                  return (
                    <React.Fragment key={`${key}-${option.identifier}`}>
                      <OptionWrapper
                        {...optionProperties}
                        optionTestIdBuilder={optionTestIdBuilder}
                        option={option}
                      >
                        <NoMatchesCard
                          title={noMatchLabel}
                          subTitle={reviseSearchLabel}
                          linkText={browseForLocationLabel}
                          link={findLocationUrl.href}
                          additionalText={helperSearchText}
                        />
                      </OptionWrapper>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={`${key}-${option.identifier}`}>
                    {/* Mobile Avis First Logo */}
                    {isAFLocalMarketChangesEnabled &&
                      isAFLocalMarketStepOneEnabled &&
                      index === 0 &&
                      options.every(
                        (opt) =>
                          opt.suggestionTypeCode === 'CURRENT' ||
                          opt.suggestionTypeCode === 'RECENT',
                      ) && (
                        <Box
                          sx={{
                            ...avisFirstDeliveryLabelContainer,
                            borderRadius: '4px',
                          }}
                        >
                          <Box>
                            <Image
                              data-testid="avis-first-logo-bookingwidget"
                              style={{
                                width: isMobile ? '70px' : '88px',
                                height: isMobile ? '10px' : '12px',
                              }}
                              src={avisFirstLogo}
                              alt="avis-first-logo"
                              width={0}
                              height={0}
                            />
                          </Box>
                          <Box
                            sx={{ display: 'flex', flexDirection: 'column' }}
                          >
                            <AbgTypography
                              variant="caption"
                              sx={{ color: '#524D4D' }}
                            >
                              {avisFirstSelectAreasLabel}
                            </AbgTypography>
                            <AbgTypography variant="bodySmallRegular">
                              {avisFirstDeliveryToYourAddressLabel}
                            </AbgTypography>
                          </Box>
                        </Box>
                      )}
                    <SuggestionOption
                      suggestion={option}
                      showInfoIcon={true}
                      onInfoClick={handleMobileInfoClick}
                      isMobile={isMobile}
                      {...optionProperties}
                      optionTestIdBuilder={optionTestIdBuilder}
                      option={option}
                      avisFirstAvailableLabel={
                        avisFirstBookingwidgetAvailableLabel
                      }
                      avisFirstDeliveryPickupLabel={
                        avisFirstDeliveryPickupLabel
                      }
                      isAFLocalMarketChangesEnabled={
                        isAFLocalMarketChangesEnabled
                      }
                      isAFLocalMarketStepOneOtherLocationLabelEnabled={
                        isAFLocalMarketStepOneOtherLocationLabelEnabled
                      }
                      multipleLocationsLabel={multipleLocationsLabel}
                    />
                  </React.Fragment>
                );
              }}
            />
          </ConfigItemFormContainerMobile>

          <LocationDetailsMobile
            isOpen={mobileDetailsDrawerOpen && !!mobileDetailsOption}
            handleClose={handleMobileDetailsDrawerClose}
            option={mobileDetailsOption || ({} as LocationSuggestionOption)}
            uiTextData={{
              ...otherLabels,
              multipleLocationsLabel,
              avisFirstCurbsideDeliveryLabel,
              avisFirstAtHomeDeliveryLabel,
            }}
            onSelectLocation={handleMobileLocationSelect}
            locationType={name === 'pickup' ? 'pickup' : 'dropoff'}
            isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
            isAFLocalMarketStepOneOtherLocationLabelEnabled={
              isAFLocalMarketStepOneOtherLocationLabelEnabled
            }
          />
        </LocationSearchWrapper>
      </ClickAwayListener>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleCloseClickOutside}>
      <LocationSearchWrapper>
        <Autocomplete<LocationSuggestionOption, false, false, true>
          disabled={disabled}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={handleClose}
          freeSolo
          filterOptions={(x) => x} // disable default filter, filtering we'll be implemented in an async request
          filterSelectedOptions // hide selected value, since it is appended to the options array
          {...field}
          value={value}
          inputValue={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          options={options as readonly LocationSuggestionOption[]}
          groupBy={
            noMatchFound ? undefined : (option) => option.suggestionGroup
          }
          renderGroup={renderGroup}
          slots={{
            popper: LocationSearchPopper as AutocompleteSlots['popper'],
          }}
          slotProps={{
            popper: {
              // @ts-expect-error TS2353
              'data-testid': `${testIdPrefix}-dropdown`,
              showLocationDetailsFeature,
              locationDetailOption,
              locationType: name === 'pickup' ? 'pickup' : 'dropoff',
              uiTextData: {
                ...otherLabels,
                multipleLocationsLabel,
                avisFirstCurbsideDeliveryLabel,
                avisFirstAtHomeDeliveryLabel,
              },
            },
          }}
          multiple={false}
          loading={loading}
          loadingText={loadingText}
          onHighlightChange={handleHighlightChange}
          onKeyDown={handleAutocompleteKeys}
          onInputChange={(_event, newInputValue, reason) => {
            setInputReason(reason);
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
          }}
          renderInput={(parameters) => (
            <LocationTextField
              disabled={disabled}
              location={value}
              defaultIcon={inputIcon}
              endAdornment={endAdornment}
              testIdSuffix="input"
              testIdPrefix={testIdPrefix}
              hasIncorrectValue={hasIncorrectValue}
              renderError={renderError}
              placeholder={placeholder}
              slotProps={{
                input: {
                  ...parameters.InputProps,
                },
                htmlInput: {
                  'aria-label': placeholder,
                  ...parameters.inputProps,
                },
              }}
            />
          )}
          getOptionLabel={(option) =>
            isSuggestion(option) ? option.locationName : option
          }
          renderOption={({ key, ...optionProperties }, option, { index }) => {
            // @TODO: refactor this condition
            if (option.typeCode === 'Error') {
              return (
                <React.Fragment key={`${key}-${option.identifier}`}>
                  <OptionWrapper
                    {...optionProperties}
                    optionTestIdBuilder={optionTestIdBuilder}
                    option={option}
                  >
                    <NoMatchesCard
                      title={noMatchLabel}
                      subTitle={reviseSearchLabel}
                      linkText={browseForLocationLabel}
                      link={findLocationUrl.href}
                      additionalText={helperSearchText}
                    />
                  </OptionWrapper>
                </React.Fragment>
              );
            }
            return (
              <React.Fragment key={`${key}-${option.identifier}`}>
                {/* Avis First Logo */}
                {isAFLocalMarketChangesEnabled &&
                  isAFLocalMarketStepOneEnabled &&
                  index === 0 &&
                  options.every(
                    (opt) =>
                      opt.suggestionTypeCode === 'CURRENT' ||
                      opt.suggestionTypeCode === 'RECENT',
                  ) && (
                    <Box sx={avisFirstDeliveryLabelContainer}>
                      <Box>
                        <Image
                          data-testid="avis-first-logo-bookingwidget"
                          style={{
                            width: isMobile ? '70px' : '88px',
                            height: isMobile ? '10px' : '12px',
                          }}
                          src={avisFirstLogo}
                          alt="avis-first-logo"
                          width={0}
                          height={0}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <AbgTypography
                          variant="caption"
                          sx={{ color: '#524D4D' }}
                        >
                          {avisFirstSelectAreasLabel}
                          {/* Now in select areas */}
                        </AbgTypography>
                        <AbgTypography variant="bodySmallRegular">
                          {avisFirstDeliveryToYourAddressLabel}
                          {/* See if we deliver to your address */}
                        </AbgTypography>
                      </Box>
                    </Box>
                  )}
                <SuggestionOption
                  suggestion={option}
                  isMobile={isMobile}
                  {...optionProperties}
                  optionTestIdBuilder={optionTestIdBuilder}
                  option={option}
                  avisFirstAvailableLabel={avisFirstBookingwidgetAvailableLabel}
                  avisFirstDeliveryPickupLabel={avisFirstDeliveryPickupLabel}
                  isAFLocalMarketChangesEnabled={isAFLocalMarketChangesEnabled}
                  isAFLocalMarketStepOneOtherLocationLabelEnabled={
                    isAFLocalMarketStepOneOtherLocationLabelEnabled
                  }
                  multipleLocationsLabel={multipleLocationsLabel}
                />
              </React.Fragment>
            );
          }}
        />
      </LocationSearchWrapper>
    </ClickAwayListener>
  );
};
